import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Typography,
  message,
  type TableProps,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  MinusCircleOutlined,
  PartitionOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  SUBANTA_BACHAN,
  SUBANTA_BARGA,
  SUBANTA_LINGA,
  SUBANTA_PRAKAR,
  SUBANTA_SHRENI,
  SUBANTA_VEDIC,
  SUBANTA_VYUTPANNA,
  enumToOptions,
} from '../constants/subantaEnums';
import { ARTHA_BIDHA } from '../constants/arthaEnums';
import { SAMASTA_TYPE } from '../constants/samastaEnums';
import {
  buildArthaPatch,
  arthaFormToCreateBody,
  createArtha,
  deleteArtha,
  isArtha,
  updateArtha,
  type Artha,
  type CreateArthaBody,
} from '../api/arthaApi';
import { listLanguages, type Language } from '../api/languageApi';
import {
  createSubanta,
  deleteSubanta,
  getSubanta,
  listSubantas,
  mergeSubantas,
  updateSubanta,
  Vedic,
  type Subanta,
  type SubantaLinga,
  type SubantaScalars,
} from '../api/subantaApi';
import {
  searchSubantaRupFormula,
  subantaRupFormulaGridToRupDeclensionFields,
} from '../api/subantaRupFormulaApi';
import {
  RUP_DECLENSION_KEYS,
  RUP_TRIPLE,
  RUP_VIBHAKTI,
  buildRupPatch,
  createRup,
  createRupBodyFromForm,
  deleteRup,
  getRup,
  isRup,
  listRups,
  rupToFormValues,
  updateRup,
  type Rup,
  type RupDeclensionKey,
  type RupFormValues,
} from '../api/rupApi';
import {
  createSamastaWord,
  deleteSamastaWord,
  updateSamastaWord,
  type PatchSamastaWordBody,
  type SamastaWord,
} from '../api/samastaWordsApi';
import {
  createSamastaCompound,
  deleteSamastaCompound,
  updateSamastaCompound,
} from '../api/samastaCompoundApi';
import { listTingantas, type Tinganta } from '../api/tingantaApi';
import {
  getCachedSubantaDetail,
  getCachedSubantaList,
  mergeSubantaDetailCache,
  mergeSubantaListCache,
  invalidateSubantaDetail,
} from '../cache/subantaCache';
import { EntryNotificationsPanel } from '../components/EntryNotificationsPanel';
import { EntryMediaPanel } from '../components/EntryMediaPanel';
import { EntryStatsPanel } from '../components/EntryStatsPanel';

type ComponentKind = 'subanta' | 'tinganta';

type ComponentWordRow = {
  componentKind: ComponentKind;
  refId: number;
  order: number;
  /** Existing `samasta-words` row id when editing; omitted for new rows. */
  samastaWordRowId?: number;
};

const COMPONENT_KIND_OPTIONS: { value: ComponentKind; label: string }[] = [
  { value: 'subanta', label: 'Subanta' },
  { value: 'tinganta', label: 'Tiṅganta' },
];
type SamastaPairRow = { type: string; firstIdx: number; secondIdx: number };

/** Add-subanta step 1: head fields + optional component words (no pairs). */
type SubantaMainFormValues = SubantaScalars & {
  hasSamastaWords?: boolean;
  componentWords?: ComponentWordRow[];
};

/** Existing samāsa compound row in the edit dialog (first/second = samasta_words.id). */
type SamastaPairEditValues = {
  compoundId?: number;
  type: string;
  first_wordId: number;
  second_wordId: number;
};

type SamastaWordRel = {
  id: number;
  order: number;
  subantaWordId?: number | null;
  tingantaWordId?: number | null;
};

function isSamastaWordRel(x: unknown): x is SamastaWordRel {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'number' && typeof o.order === 'number';
}

function isSamastaCompoundRel(
  x: unknown
): x is {
  id: number;
  type: string;
  first_wordId: number;
  second_wordId: number;
} {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'number' &&
    typeof o.type === 'string' &&
    typeof o.first_wordId === 'number' &&
    typeof o.second_wordId === 'number'
  );
}

/** Dropdown / list label: underlying word only + (S)ubanta or (T)iṅganta. Value is still samasta_word id. */
function formatSamastaWordSlotDisplay(
  w: SamastaWordRel,
  subantas: Subanta[],
  tingantas: Tinganta[]
): string {
  if (w.subantaWordId != null) {
    const s = subantas.find((r) => r.id === w.subantaWordId);
    if (s) return `${s.word} · ${s.iast} (S)`;
    return `? (S)`;
  }
  if (w.tingantaWordId != null) {
    const t = tingantas.find((x) => x.id === w.tingantaWordId);
    if (t) {
      const word =
        [t.dhatu, t.aupadeshik].filter(Boolean).join(' · ') ||
        t.artha ||
        t.artha_english ||
        '?';
      return `${word} (T)`;
    }
    return `? (T)`;
  }
  return '—';
}

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

/** Nested `subantaWord` from API relation payload. */
function formatNestedSubantaWordNode(sw: unknown): string {
  if (!isPlainObject(sw)) return '—';
  const word = sw.word;
  const iast = sw.iast;
  const id = sw.id;
  if (typeof word === 'string' && typeof iast === 'string') {
    return `${word} · ${iast} (S) · #${typeof id === 'number' ? id : '?'}`;
  }
  if (typeof id === 'number') return `Subanta #${id}`;
  return '—';
}

/** Nested `tingantaWord` from API relation payload. */
function formatNestedTingantaWordNode(tw: unknown): string {
  if (!isPlainObject(tw)) return '—';
  const dhatu = tw.dhatu;
  const aup = tw.aupadeshik;
  const artha = tw.artha;
  const id = tw.id;
  const stem =
    [typeof dhatu === 'string' ? dhatu : '', typeof aup === 'string' ? String(aup) : '']
      .filter(Boolean)
      .join(' · ') ||
    (typeof artha === 'string' ? artha : null) ||
    '?';
  return `${stem} (T) · #${typeof id === 'number' ? id : '?'}`;
}

/** One samasta_words row from API (includes nested subantaWord / tingantaWord). */
function formatSamastaWordRelationRow(row: unknown): string {
  if (!isPlainObject(row)) return '—';
  if (row.subantaWord != null) return formatNestedSubantaWordNode(row.subantaWord);
  if (row.tingantaWord != null) return formatNestedTingantaWordNode(row.tingantaWord);
  return '—';
}

function samastaWordRelationKind(row: unknown): string {
  if (!isPlainObject(row)) return '—';
  if (row.subantaWordId != null) return 'S';
  if (row.tingantaWordId != null) return 'T';
  return '—';
}

function samastaWordRelationRefId(row: unknown): string {
  if (!isPlainObject(row)) return '—';
  if (typeof row.subantaWordId === 'number') return String(row.subantaWordId);
  if (typeof row.tingantaWordId === 'number') return String(row.tingantaWordId);
  return '—';
}

const DETAIL_SAMASTA_WORD_COLUMNS: ColumnsType<Record<string, unknown>> = [
  { title: 'ID', dataIndex: 'id', width: 64 },
  { title: 'Order', dataIndex: 'order', width: 72 },
  {
    title: 'S/T',
    key: 'kind',
    width: 48,
    render: (_, r) => samastaWordRelationKind(r),
  },
  {
    title: 'Word',
    key: 'word',
    ellipsis: true,
    render: (_, r) => formatSamastaWordRelationRow(r),
  },
  {
    title: 'Sub./Tiṅ. id',
    key: 'ref',
    width: 88,
    render: (_, r) => samastaWordRelationRefId(r),
  },
];

const DETAIL_SAMASTA_COMPOUND_COLUMNS: ColumnsType<Record<string, unknown>> = [
  { title: 'ID', dataIndex: 'id', width: 64 },
  { title: 'Type', dataIndex: 'type', width: 140, ellipsis: true },
  {
    title: 'First word',
    key: 'first',
    ellipsis: true,
    render: (_, r) => formatSamastaWordRelationRow(r.first_word),
  },
  {
    title: 'Second word',
    key: 'second',
    ellipsis: true,
    render: (_, r) => formatSamastaWordRelationRow(r.second_word),
  },
];

function renderOtherRelationSection(label: string, value: unknown): React.ReactNode {
  const arr = Array.isArray(value) ? value : null;
  if (!arr || arr.length === 0) {
    return (
      <Typography.Paragraph type="secondary" style={{ margin: '4px 0' }}>
        <strong>{label}:</strong> none
      </Typography.Paragraph>
    );
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <Typography.Text strong>{label}</Typography.Text>
      <Typography.Text type="secondary"> ({arr.length})</Typography.Text>
      <pre
        style={{
          marginTop: 6,
          maxHeight: 220,
          overflow: 'auto',
          fontSize: 11,
          background: '#fafafa',
          padding: 8,
          borderRadius: 6,
        }}
      >
        {JSON.stringify(arr, null, 2)}
      </pre>
    </div>
  );
}

function hasSamastaListData(record: Subanta): boolean {
  const sw = record.samastaWords;
  const sa = record.samastas;
  return (
    (Array.isArray(sw) && sw.length > 0) || (Array.isArray(sa) && sa.length > 0)
  );
}

function samastaRelToComponentRow(rel: SamastaWordRel): ComponentWordRow | null {
  if (rel.subantaWordId != null) {
    return {
      componentKind: 'subanta',
      refId: rel.subantaWordId,
      order: rel.order,
      samastaWordRowId: rel.id,
    };
  }
  if (rel.tingantaWordId != null) {
    return {
      componentKind: 'tinganta',
      refId: rel.tingantaWordId,
      order: rel.order,
      samastaWordRowId: rel.id,
    };
  }
  return null;
}

function parseSamastaDetailForEdit(s: Subanta): {
  sortedWords: SamastaWordRel[];
  initialPairs: SamastaPairEditValues[];
} {
  const rawWords = s.samastaWords;
  const arr = Array.isArray(rawWords) ? rawWords : [];
  const sortedWords = arr
    .filter(isSamastaWordRel)
    .slice()
    .sort((a, b) => Number(a.order) - Number(b.order));
  const wordIds = new Set(sortedWords.map((w) => w.id));

  const rawPairs = s.samastas;
  const pairsArr = Array.isArray(rawPairs) ? rawPairs : [];
  const initialPairs: SamastaPairEditValues[] = [];
  for (const p of pairsArr) {
    if (!isSamastaCompoundRel(p)) continue;
    if (!wordIds.has(p.first_wordId) || !wordIds.has(p.second_wordId)) continue;
    initialPairs.push({
      compoundId: p.id,
      type: p.type,
      first_wordId: p.first_wordId,
      second_wordId: p.second_wordId,
    });
  }
  return { sortedWords, initialPairs };
}

const SCALAR_KEYS: (keyof SubantaScalars)[] = [
  'word',
  'base_word',
  'iast',
  'bat',
  'linga',
  'vyutpanna',
  'prakar',
  'shreni',
  'shatwa',
  'natwa',
  'vedic',
  'barga',
  'bachan',
];

function pickScalars(s: Subanta): SubantaScalars {
  const o = {} as Record<keyof SubantaScalars, SubantaScalars[keyof SubantaScalars]>;
  for (const key of SCALAR_KEYS) {
    o[key] = s[key];
  }
  return o as SubantaScalars;
}

function scalarEqualForPatch(key: keyof SubantaScalars, a: unknown, b: unknown): boolean {
  if (key === 'shatwa' || key === 'natwa') {
    return Boolean(a) === Boolean(b);
  }
  if (key === 'bat') {
    const empty = (v: unknown) => v == null || String(v).trim() === '';
    if (empty(a) && empty(b)) return true;
    return String(a ?? '').trim() === String(b ?? '').trim();
  }
  return a === b;
}

function samastaWordApiToRel(sw: SamastaWord): SamastaWordRel {
  return {
    id: sw.id,
    order: sw.order,
    subantaWordId: sw.subantaWordId ?? null,
    tingantaWordId: sw.tingantaWordId ?? null,
  };
}

/** POST /samasta-words for each component row (sorted by order). No compounds. */
async function createSamastaWordRowsOnly(
  subantaId: number,
  componentWords: ComponentWordRow[] | undefined
): Promise<SamastaWord[]> {
  const comps = (componentWords ?? []).filter((c) => {
    if (c == null || !Number.isFinite(Number(c.order))) return false;
    const kind = c.componentKind;
    if (kind !== 'subanta' && kind !== 'tinganta') return false;
    return Number.isFinite(Number(c.refId));
  });
  for (const c of comps) {
    if (c.componentKind === 'subanta' && Number(c.refId) === subantaId) {
      throw new Error(
        `subantaWordId must not equal this compound head subanta id (${subantaId}).`
      );
    }
  }
  const sorted = [...comps].sort((a, b) => Number(a.order) - Number(b.order));
  const created: SamastaWord[] = [];
  for (const row of sorted) {
    const order = Number(row.order);
    const sw =
      row.componentKind === 'subanta'
        ? await createSamastaWord({
            subantaId,
            order,
            subantaWordId: Number(row.refId),
          })
        : await createSamastaWord({
            subantaId,
            order,
            tingantaWordId: Number(row.refId),
          });
    created.push(sw);
  }
  return created;
}

async function createSamastaChain(
  subantaId: number,
  hasSamastaWords: boolean,
  componentWords: ComponentWordRow[] | undefined,
  samastaPairs: SamastaPairRow[] | undefined
) {
  if (!hasSamastaWords) return;
  const comps = (componentWords ?? []).filter((c) => {
    if (c == null || !Number.isFinite(Number(c.order))) return false;
    const kind = c.componentKind;
    if (kind !== 'subanta' && kind !== 'tinganta') return false;
    return Number.isFinite(Number(c.refId));
  });
  const pairs = (samastaPairs ?? []).filter(
    (p) =>
      p != null &&
      String(p.type || '').length > 0 &&
      Number.isFinite(Number(p.firstIdx)) &&
      Number.isFinite(Number(p.secondIdx))
  );
  if (pairs.length === 0 && comps.length === 0) return;
  if (pairs.length > 0 && comps.length < 2) {
    throw new Error('Add at least two component words when defining samasta pairs.');
  }
  for (const c of comps) {
    if (c.componentKind === 'subanta' && Number(c.refId) === subantaId) {
      throw new Error(
        `subantaWordId must not equal this compound head subanta id (${subantaId}).`
      );
    }
  }
  const sorted = [...comps].sort((a, b) => Number(a.order) - Number(b.order));
  const swIds: number[] = [];
  for (const row of sorted) {
    const order = Number(row.order);
    const sw =
      row.componentKind === 'subanta'
        ? await createSamastaWord({
            subantaId,
            order,
            subantaWordId: Number(row.refId),
          })
        : await createSamastaWord({
            subantaId,
            order,
            tingantaWordId: Number(row.refId),
          });
    swIds.push(sw.id);
  }
  for (const p of pairs) {
    const fi = Number(p.firstIdx);
    const si = Number(p.secondIdx);
    if (fi < 0 || fi >= swIds.length || si < 0 || si >= swIds.length || fi === si) {
      throw new Error(
        `Invalid pair indices ${fi}, ${si}: need distinct indices in 0..${swIds.length - 1} (sorted component rows).`
      );
    }
    await createSamastaCompound({
      type: p.type,
      first_wordId: swIds[fi],
      second_wordId: swIds[si],
      subantaId,
    });
  }
}

function buildPatch(prev: Subanta, next: SubantaScalars): Partial<SubantaScalars> {
  const patch: Partial<SubantaScalars> = {};
  for (const key of SCALAR_KEYS) {
    if (!scalarEqualForPatch(key, prev[key], next[key])) {
      (patch as Record<string, unknown>)[key] = next[key];
    }
  }
  return patch;
}

function coerceSamastaWordRowId(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeComponentWordsForCompare(rows: ComponentWordRow[] | undefined): string {
  const list = (rows ?? []).filter((c) => {
    if (c == null || !Number.isFinite(Number(c.order))) return false;
    if (c.componentKind !== 'subanta' && c.componentKind !== 'tinganta') return false;
    return Number.isFinite(Number(c.refId));
  });
  return JSON.stringify(
    [...list]
      .sort(
        (a, b) =>
          Number(a.order) - Number(b.order) ||
          Number(a.refId) - Number(b.refId) ||
          (coerceSamastaWordRowId(a.samastaWordRowId) ?? 0) -
            (coerceSamastaWordRowId(b.samastaWordRowId) ?? 0)
      )
      .map((r) => ({
        id: coerceSamastaWordRowId(r.samastaWordRowId),
        k: r.componentKind,
        ref: Number(r.refId),
        o: Number(r.order),
      }))
  );
}

async function syncSamastaWordsForEdit(
  headId: number,
  hasFlag: boolean,
  nextRows: ComponentWordRow[] | undefined,
  initialRows: ComponentWordRow[]
): Promise<void> {
  const validNext = (nextRows ?? []).filter((c) => {
    if (c == null || !Number.isFinite(Number(c.order))) return false;
    if (c.componentKind !== 'subanta' && c.componentKind !== 'tinganta') return false;
    return Number.isFinite(Number(c.refId));
  });
  for (const c of validNext) {
    if (c.componentKind === 'subanta' && Number(c.refId) === headId) {
      throw new Error(
        `subantaWordId must not equal this compound head subanta id (${headId}).`
      );
    }
  }

  if (!hasFlag) {
    for (const row of initialRows) {
      const initialId = coerceSamastaWordRowId(row.samastaWordRowId);
      if (initialId != null) {
        await deleteSamastaWord(initialId);
      }
    }
    return;
  }

  const nextIds = new Set(
    validNext.map((n) => coerceSamastaWordRowId(n.samastaWordRowId)).filter((id): id is number => id != null)
  );
  for (const row of initialRows) {
    const initialId = coerceSamastaWordRowId(row.samastaWordRowId);
    if (initialId != null && !nextIds.has(initialId)) {
      await deleteSamastaWord(initialId);
    }
  }

  for (const row of validNext) {
    const order = Number(row.order);
    const rowId = coerceSamastaWordRowId(row.samastaWordRowId);
    if (rowId == null) {
      if (row.componentKind === 'subanta') {
        await createSamastaWord({
          subantaId: headId,
          order,
          subantaWordId: Number(row.refId),
        });
      } else {
        await createSamastaWord({
          subantaId: headId,
          order,
          tingantaWordId: Number(row.refId),
        });
      }
      continue;
    }
    const prev = initialRows.find((i) => coerceSamastaWordRowId(i.samastaWordRowId) === rowId);
    if (!prev) continue;
    const patchW: PatchSamastaWordBody = {};
    if (prev.order !== order) patchW.order = order;
    if (prev.componentKind !== row.componentKind || prev.refId !== row.refId) {
      if (row.componentKind === 'subanta') {
        patchW.subantaWordId = Number(row.refId);
        patchW.tingantaWordId = null;
      } else {
        patchW.tingantaWordId = Number(row.refId);
        patchW.subantaWordId = null;
      }
    }
    if (Object.keys(patchW).length > 0) {
      await updateSamastaWord(rowId, patchW);
    }
  }
}

function subantaSearchBlob(r: Subanta): string {
  return [
    r.id,
    r.word,
    r.base_word,
    r.iast,
    r.bat,
    r.linga,
    r.vyutpanna,
    r.prakar,
    r.shreni,
    r.barga,
    r.bachan,
    r.vedic,
    r.shatwa,
    r.natwa,
  ]
    .map((x) => (x == null ? '' : String(x)).toLowerCase())
    .join('\0');
}

type SubantaSearchField =
  | 'id'
  | 'word'
  | 'base_word'
  | 'iast'
  | 'bat'
  | 'linga'
  | 'vyutpanna'
  | 'prakar'
  | 'shreni'
  | 'barga'
  | 'bachan'
  | 'vedic'
  | 'shatwa'
  | 'natwa';

type SubantaSearchMode = 'similar' | 'exact';

function subantaSearchValueByField(r: Subanta, field: SubantaSearchField): string {
  return String(r[field] ?? '').toLowerCase();
}

function normalizeSubantaScalarsForApi(values: SubantaScalars): SubantaScalars {
  const batRaw = values.bat;
  const batStr = typeof batRaw === 'string' ? batRaw.trim() : '';
  return {
    ...values,
    bat: batStr === '' ? null : batStr,
  };
}

function strSorter(a: unknown, b: unknown): number {
  return String(a ?? '').localeCompare(String(b ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/** Same field order as the main subanta table column sorters (for prev/next in details modal). */
type SubantaListSortField =
  | 'id'
  | 'word'
  | 'base_word'
  | 'iast'
  | 'bat'
  | 'linga'
  | 'barga'
  | 'bachan';

const SUBANTA_LIST_SORT_FIELDS: readonly SubantaListSortField[] = [
  'id',
  'word',
  'base_word',
  'iast',
  'bat',
  'linga',
  'barga',
  'bachan',
] as const;

function isSubantaListSortField(f: unknown): f is SubantaListSortField {
  return typeof f === 'string' && (SUBANTA_LIST_SORT_FIELDS as readonly string[]).includes(f);
}

function compareSubantasForListSort(a: Subanta, b: Subanta, field: SubantaListSortField): number {
  switch (field) {
    case 'id':
      return Number(a.id) - Number(b.id);
    case 'word':
      return strSorter(a.word, b.word);
    case 'base_word':
      return strSorter(a.base_word, b.base_word);
    case 'iast':
      return strSorter(a.iast, b.iast);
    case 'bat':
      return strSorter(a.bat, b.bat);
    case 'linga':
      return strSorter(a.linga, b.linga);
    case 'barga':
      return strSorter(a.barga, b.barga);
    case 'bachan':
      return strSorter(a.bachan, b.bachan);
    default: {
      const _exhaust: never = field;
      return _exhaust;
    }
  }
}

/** Sort arthas for display / reorder: explicit `order` first, then id. */
function compareArthaDisplayOrder(a: Artha, b: Artha): number {
  const ao = a.order;
  const bo = b.order;
  if (ao != null && bo != null && ao !== bo) return ao - bo;
  if (ao != null && bo == null) return -1;
  if (ao == null && bo != null) return 1;
  return a.id - b.id;
}

function arthaOriginGroupKey(v: string | null | undefined): string {
  const t = String(v ?? '').trim();
  return t === '' ? '__no_origin__' : t.toLowerCase();
}

function arthaOriginGroupLabel(v: string | null | undefined): string {
  const t = String(v ?? '').trim();
  return t === '' ? 'No origin' : t;
}

/** Separate `/rup` rows when head subanta `linga` is `trilinga`. */
const TRILINGA_RUP_LINGAS = ['pumalinga', 'stri_linga', 'napumsak_linga'] as const;

type TrilingaRupLingaKey = (typeof TRILINGA_RUP_LINGAS)[number];

function trilingaRupSectionTitle(lk: TrilingaRupLingaKey): string {
  if (lk === 'pumalinga') return 'Puṃlinga';
  if (lk === 'stri_linga') return 'Strīliṅga';
  return 'Napuṃsakaliṅga';
}

function isTrilingaTripleSlotsAvailable(rups: Rup[]): boolean {
  const used = new Set(rups.map((r) => r.linga));
  return TRILINGA_RUP_LINGAS.every((l) => !used.has(l));
}

function rupValuesFromTripleRowCells(linga: SubantaLinga, cells: unknown): RupFormValues {
  const o =
    cells != null && typeof cells === 'object'
      ? (cells as Partial<Record<RupDeclensionKey, string | undefined>>)
      : {};
  const out = { linga } as RupFormValues;
  for (const key of RUP_DECLENSION_KEYS) {
    out[key] = o[key];
  }
  return out;
}

/** Full declension grid shown when expanding a rup row in the subanta detail modal. */
function RupDeclensionInspectExpanded({ r }: { r: Rup }) {
  return (
    <div style={{ padding: '4px 0 12px 28px' }}>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            minWidth: 520,
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th style={{ border: '1px solid #f0f0f0', padding: 4, background: '#fafafa' }} />
              {RUP_TRIPLE.map((t) => (
                <th
                  key={t}
                  style={{
                    border: '1px solid #f0f0f0',
                    padding: 6,
                    background: '#fafafa',
                    textAlign: 'left',
                  }}
                >
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RUP_VIBHAKTI.map((v) => (
              <tr key={v}>
                <td
                  style={{
                    border: '1px solid #f0f0f0',
                    padding: 4,
                    background: '#fafafa',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {v}
                </td>
                {RUP_TRIPLE.map((t) => {
                  const k = `${v}_${t}` as RupDeclensionKey;
                  const val = r[k];
                  const display =
                    val != null && String(val).trim() !== '' ? String(val) : '—';
                  return (
                    <td
                      key={k}
                      style={{
                        border: '1px solid #f0f0f0',
                        padding: 4,
                        wordBreak: 'break-word',
                        maxWidth: 220,
                      }}
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type RupModalFormValues = RupFormValues & {
  triple?: Partial<
    Record<(typeof TRILINGA_RUP_LINGAS)[number], Partial<Record<RupDeclensionKey, string | undefined>>>
  >;
};

export function SubantasPage() {
  const [rows, setRows] = useState<Subanta[]>(() => getCachedSubantaList() ?? []);
  const [tingantaRows, setTingantaRows] = useState<Tinganta[]>([]);
  const [loading, setLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);
  const [listSearchInput, setListSearchInput] = useState('');
  const [listSearch, setListSearch] = useState('');
  const [listSearchFields, setListSearchFields] = useState<SubantaSearchField[]>([]);
  const [listSearchMode, setListSearchMode] = useState<SubantaSearchMode>('similar');
  const [listLingaFilter, setListLingaFilter] = useState<SubantaLinga | undefined>(undefined);
  const [listSortByHindiOrder, setListSortByHindiOrder] = useState(false);
  /** Mirrors the main table sort so detail prev/next matches visible row order. */
  const [listTableSort, setListTableSort] = useState<{
    field: SubantaListSortField;
    order: 'ascend' | 'descend';
  }>({ field: 'id', order: 'ascend' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Subanta | null>(null);
  const [saving, setSaving] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [editPrefillLoading, setEditPrefillLoading] = useState(false);
  const editInitialComponentWordsRef = useRef<ComponentWordRow[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Subanta | null>(null);
  const [form] = Form.useForm<SubantaMainFormValues>();
  const hasSamastaWordsWatched = Form.useWatch('hasSamastaWords', form);

  const [samastaEditOpen, setSamastaEditOpen] = useState(false);
  const [samastaEditLoading, setSamastaEditLoading] = useState(false);
  const [samastaEditSaving, setSamastaEditSaving] = useState(false);
  const [samastaEditSubantaId, setSamastaEditSubantaId] = useState<number | null>(null);
  const [samastaEditHeadLabel, setSamastaEditHeadLabel] = useState('');
  const [samastaEditSortedWords, setSamastaEditSortedWords] = useState<SamastaWordRel[]>([]);
  const [samastaEditInitialCompoundIds, setSamastaEditInitialCompoundIds] = useState<Set<number>>(
    () => new Set()
  );
  const [samastaEditForm] = Form.useForm<{ pairs: SamastaPairEditValues[] }>();

  const [languageRows, setLanguageRows] = useState<Language[]>([]);
  const [arthaModalOpen, setArthaModalOpen] = useState(false);
  const [arthaModalSaving, setArthaModalSaving] = useState(false);
  const [arthaEditing, setArthaEditing] = useState<Artha | null>(null);
  const [arthaHeadSubantaId, setArthaHeadSubantaId] = useState<number | null>(null);
  const [arthaForm] = Form.useForm<Omit<CreateArthaBody, 'subantaId'>>();
  /** Active language tab in subanta detail modal (arthas grouped by languageId). */
  const [detailArthaLanguageTabKey, setDetailArthaLanguageTabKey] = useState<string>('');
  const [arthaReorderSaving, setArthaReorderSaving] = useState(false);

  const [detailRups, setDetailRups] = useState<Rup[]>([]);
  const [rupModalOpen, setRupModalOpen] = useState(false);
  const [rupModalSaving, setRupModalSaving] = useState(false);
  const [rupEditing, setRupEditing] = useState<Rup | null>(null);
  const [rupPrefillLoading, setRupPrefillLoading] = useState(false);
  const [rupHeadSubantaId, setRupHeadSubantaId] = useState<number | null>(null);
  const [rupForm] = Form.useForm<RupModalFormValues>();
  /** Apply after Add rup modal mounts (`destroyOnClose` otherwise drops early `setFieldsValue`). */
  const rupCreatePreferredLingaRef = useRef<SubantaLinga | null>(null);
  const watchedRupLinga = Form.useWatch('linga', rupForm);
  const [rupFormulaSearchLoading, setRupFormulaSearchLoading] = useState(false);
  const [rupFormulaBanner, setRupFormulaBanner] = useState<string | null>(null);
  /** Add-rup UI: three grids → three POST /rup when head subanta is trilinga and slots are free. */
  const [rupTripleCreateUi, setRupTripleCreateUi] = useState(false);
  const [mergeForm] = Form.useForm<{ srcId: number; destinationId: number }>();

  const lingaOpts = useMemo(() => enumToOptions(SUBANTA_LINGA), []);
  const searchFieldOptions = useMemo(
    () => [
      { value: 'word', label: 'Word' },
      { value: 'base_word', label: 'Base word' },
      { value: 'iast', label: 'IAST' },
      { value: 'bat', label: 'BAT' },
      { value: 'linga', label: 'Liṅga' },
      { value: 'vyutpanna', label: 'Vyutpanna' },
      { value: 'prakar', label: 'Prakār' },
      { value: 'shreni', label: 'Śreṇi' },
      { value: 'barga', label: 'Barga' },
      { value: 'bachan', label: 'Bachan' },
      { value: 'vedic', label: 'Vedic' },
      { value: 'shatwa', label: 'Śatva' },
      { value: 'natwa', label: 'Natva' },
      { value: 'id', label: 'ID' },
    ],
    []
  );
  const searchModeOptions = useMemo(
    () => [
      { value: 'similar', label: 'Similar (contains)' },
      { value: 'exact', label: 'Exact match' },
    ],
    []
  );
  const vyutOpts = useMemo(() => enumToOptions(SUBANTA_VYUTPANNA), []);
  const prakarOpts = useMemo(() => enumToOptions(SUBANTA_PRAKAR), []);
  const shreniOpts = useMemo(() => enumToOptions(SUBANTA_SHRENI), []);
  const bargaOpts = useMemo(() => enumToOptions(SUBANTA_BARGA), []);
  const bachanOpts = useMemo(() => enumToOptions(SUBANTA_BACHAN), []);
  const vedicOpts = useMemo(() => enumToOptions(SUBANTA_VEDIC), []);
  const samastaTypeOpts = useMemo(() => enumToOptions(SAMASTA_TYPE), []);
  const bidhaOpts = useMemo(() => enumToOptions(ARTHA_BIDHA), []);

  const languageSelectOptions = useMemo(
    () =>
      [...languageRows]
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .map((l) => ({ value: l.id, label: `${l.name} (${l.code})` })),
    [languageRows]
  );

  const subantaWordSelectOptions = useMemo(
    () =>
      [...rows]
        .sort((a, b) =>
          a.word.localeCompare(b.word, undefined, { sensitivity: 'base', numeric: true })
        )
        .map((s) => ({
          value: s.id,
          label: `${s.word} · ${s.iast} (#${s.id})`,
        })),
    [rows]
  );

  const mergeSubantaOptions = useMemo(
    () =>
      [...rows]
        .map((r) => ({
          value: r.id,
          label: `${r.word} (${r.iast}) · #${r.id}`,
        }))
        .sort((a, b) => strSorter(a.label, b.label)),
    [rows]
  );

  const tingantaWordSelectOptions = useMemo(() => {
    const labelFor = (t: Tinganta) => {
      const parts = [t.dhatu, t.aupadeshik].filter(Boolean);
      const stem =
        parts.length > 0 ? parts.join(' · ') : t.artha || t.artha_english || '';
      return stem ? `${stem} (#${t.id})` : `Tiṅganta #${t.id}`;
    };
    return [...tingantaRows]
      .sort((a, b) =>
        labelFor(a).localeCompare(labelFor(b), undefined, { sensitivity: 'base', numeric: true })
      )
      .map((t) => ({ value: t.id, label: labelFor(t) }));
  }, [tingantaRows]);

  const samastaWordSlotOptionsForEdit = useMemo(
    () =>
      samastaEditSortedWords.map((w) => ({
        value: w.id,
        label: formatSamastaWordSlotDisplay(w, rows, tingantaRows),
      })),
    [samastaEditSortedWords, rows, tingantaRows]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, ting, langs] = await Promise.all([
        listSubantas(),
        listTingantas(),
        listLanguages(),
      ]);
      const { changed, next } = mergeSubantaListCache(data);
      if (changed) setRows(next);
      setTingantaRows(ting);
      setLanguageRows(langs);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        ax?.response?.data?.message || ax?.message || 'Failed to load subantas, tingantas, or languages'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setListSearch(listSearchInput);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [listSearchInput]);

  const filteredSubantaRows = useMemo(() => {
    const q = listSearch.trim().toLowerCase();
    return rows.filter((r) => {
      if (listLingaFilter && r.linga !== listLingaFilter) return false;
      if (!q) return true;
      if (listSearchFields.length === 0) {
        const target = subantaSearchBlob(r);
        if (listSearchMode === 'exact') return target === q;
        return target.includes(q);
      }
      if (listSearchMode === 'exact') {
        return listSearchFields.some((field) => subantaSearchValueByField(r, field) === q);
      }
      return listSearchFields.some((field) => subantaSearchValueByField(r, field).includes(q));
    });
  }, [rows, listSearch, listLingaFilter, listSearchFields, listSearchMode]);

  const displayedSubantaRows = useMemo(() => {
    if (!listSortByHindiOrder) return filteredSubantaRows;
    return [...filteredSubantaRows].sort((a, b) => {
      const ao = a.hindi_order;
      const bo = b.hindi_order;
      const an = typeof ao === 'number' ? ao : Number(ao);
      const bn = typeof bo === 'number' ? bo : Number(bo);
      const aOk = Number.isFinite(an);
      const bOk = Number.isFinite(bn);
      if (!aOk && !bOk) return a.id - b.id;
      if (!aOk) return 1;
      if (!bOk) return -1;
      if (an !== bn) return an - bn;
      return a.id - b.id;
    });
  }, [filteredSubantaRows, listSortByHindiOrder]);

  const tableOrderedFilteredSubantas = useMemo(() => {
    const copy = [...filteredSubantaRows];
    const { field, order } = listTableSort;
    copy.sort((a, b) => {
      const cmp = compareSubantasForListSort(a, b, field);
      return order === 'descend' ? -cmp : cmp;
    });
    return copy;
  }, [filteredSubantaRows, listTableSort]);

  /** Prev/next follow the same order as the sorted table (not raw API order). */
  const detailNav = useMemo(() => {
    const id = detail?.id;
    if (id == null) {
      return { prevId: null as number | null, nextId: null as number | null, index: -1, total: 0 };
    }
    const inFiltered = filteredSubantaRows.some((r) => r.id === id);
    const ordered = inFiltered
      ? tableOrderedFilteredSubantas
      : [...rows].sort((a, b) => {
          const cmp = compareSubantasForListSort(a, b, listTableSort.field);
          return listTableSort.order === 'descend' ? -cmp : cmp;
        });
    const ids = ordered.map((r) => r.id);
    const idx = ids.indexOf(id);
    if (idx < 0) {
      return { prevId: null, nextId: null, index: -1, total: ids.length };
    }
    return {
      prevId: idx > 0 ? ids[idx - 1]! : null,
      nextId: idx < ids.length - 1 ? ids[idx + 1]! : null,
      index: idx,
      total: ids.length,
    };
  }, [detail?.id, filteredSubantaRows, rows, tableOrderedFilteredSubantas, listTableSort]);

  const listTotalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredSubantaRows.length / listPageSize)),
    [filteredSubantaRows.length, listPageSize]
  );

  const listPageOptions = useMemo(
    () =>
      Array.from({ length: listTotalPages }, (_, idx) => ({
        value: idx + 1,
        label: `Page ${idx + 1}`,
      })),
    [listTotalPages]
  );

  useEffect(() => {
    setListPage(1);
  }, [listSearch, listLingaFilter, listSearchFields, listSearchMode, listSortByHindiOrder]);

  useEffect(() => {
    setListPage((p) => (p > listTotalPages ? listTotalPages : p));
  }, [listTotalPages]);

  const openCreate = () => {
    editInitialComponentWordsRef.current = [];
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      vyutpanna: 'vyutpanna',
      prakar: 'adanata',
      shreni: 'samanya',
      barga: 'naam',
      bachan: 'ekbachan_duibachan_bahubachan',
    });
    setModalOpen(true);
  };

  const openEdit = async (record: Subanta) => {
    setEditing(record);
    editInitialComponentWordsRef.current = [];
    form.resetFields();
    form.setFieldsValue({
      word: record.word,
      base_word: record.base_word,
      iast: record.iast,
      bat: record.bat ?? null,
      linga: record.linga,
      vyutpanna: record.vyutpanna,
      prakar: record.prakar,
      shreni: record.shreni,
      shatwa: record.shatwa,
      natwa: record.natwa,
      vedic: record.vedic,
      barga: record.barga,
      bachan: record.bachan,
      hasSamastaWords: false,
      componentWords: [],
    });
    setModalOpen(true);
    setEditPrefillLoading(true);
    try {
      const s = await getSubanta(record.id);
      mergeSubantaDetailCache(record.id, s);
      setEditing(s);
      const { sortedWords } = parseSamastaDetailForEdit(s);
      const componentWords = sortedWords
        .map(samastaRelToComponentRow)
        .filter((r): r is ComponentWordRow => r != null);
      const hasSamasta = componentWords.length > 0;
      editInitialComponentWordsRef.current = componentWords.map((r) => ({ ...r }));
      form.setFieldsValue({
        word: s.word,
        base_word: s.base_word,
        iast: s.iast,
        bat: s.bat ?? null,
        linga: s.linga,
        vyutpanna: s.vyutpanna,
        prakar: s.prakar,
        shreni: s.shreni,
        shatwa: s.shatwa,
        natwa: s.natwa,
        vedic: s.vedic,
        barga: s.barga,
        bachan: s.bachan,
        hasSamastaWords: hasSamasta,
        componentWords: hasSamasta ? componentWords : [],
      });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        ax?.response?.data?.message || ax?.message || 'Failed to load subanta for edit'
      );
      editInitialComponentWordsRef.current = [];
    } finally {
      setEditPrefillLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    editInitialComponentWordsRef.current = [];
    form.resetFields();
  };

  const goToSamastaPairsStep = async () => {
    try {
      const values = await form.validateFields();
      if (!values.hasSamastaWords) {
        message.info('Turn on samasta component words first, or use Create subanta.');
        return;
      }
      setSaving(true);
      const { componentWords, hasSamastaWords: _hs, ...scalars } = values as SubantaMainFormValues;
      void _hs;
      const normalizedScalars = normalizeSubantaScalarsForApi(scalars);
      let created: Subanta;
      try {
        created = await createSubanta(normalizedScalars);
      } catch (err: unknown) {
        if ((err as { errorFields?: unknown }).errorFields) return;
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to create subanta');
        return;
      }
      try {
        const swRows = await createSamastaWordRowsOnly(created.id, componentWords);
        const sortedWords = swRows.map(samastaWordApiToRel);
        setModalOpen(false);
        setEditing(null);
        form.resetFields();
        samastaEditForm.resetFields();
        setSamastaEditSubantaId(created.id);
        setSamastaEditHeadLabel(`${created.word} (${created.iast}) · #${created.id}`);
        setSamastaEditSortedWords(sortedWords);
        setSamastaEditInitialCompoundIds(new Set());
        samastaEditForm.setFieldsValue({ pairs: [] });
        setSamastaEditLoading(false);
        setSamastaEditOpen(true);
        message.success('Subanta and component words saved. Add samāsa pairs below.');
        await load();
      } catch (chainErr: unknown) {
        const msg =
          chainErr instanceof Error ? chainErr.message : String(chainErr);
        message.error(
          `Subanta #${created.id} was saved, but samasta words step failed: ${msg}`
        );
        closeModal();
        await load();
      }
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitCreateWithoutSamanta = async () => {
    try {
      const values = await form.validateFields();
      const draft = values as SubantaMainFormValues;
      if (draft.hasSamastaWords) {
        message.info('Use Next to set up samāsa pairs.');
        return;
      }
      setSaving(true);
      const { hasSamastaWords, componentWords, ...scalars } = draft;
      void hasSamastaWords;
      const created = await createSubanta(normalizeSubantaScalarsForApi(scalars));
      try {
        await createSamastaChain(created.id, false, componentWords, undefined);
        message.success('Subanta created successfully');
      } catch (chainErr: unknown) {
        const msg =
          chainErr instanceof Error ? chainErr.message : String(chainErr);
        message.error(
          `Subanta #${created.id} was saved, but samasta words step failed: ${msg}`
        );
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitEdit = async () => {
    const head = editing;
    if (head == null) {
      message.error('No subanta loaded. Close the dialog and open Edit again.');
      return;
    }
    try {
      const values = await form.validateFields();
      const main = values as SubantaMainFormValues;
      const hasFlag = Boolean(main.hasSamastaWords);
      const initialRows = editInitialComponentWordsRef.current;
      const initialHad = initialRows.length > 0;
      const nextEffective = hasFlag ? main.componentWords ?? [] : [];

      const { hasSamastaWords: _hs, componentWords: _cw, ...rawScalars } = main;
      void _hs;
      void _cw;
      const mergedScalars = {
        ...pickScalars(head),
        ...rawScalars,
      } as SubantaScalars;
      const patch = buildPatch(head, normalizeSubantaScalarsForApi(mergedScalars));
      const subantaChanged = Object.keys(patch).length > 0;
      const wordsChanged =
        hasFlag !== initialHad ||
        normalizeComponentWordsForCompare(nextEffective) !==
          normalizeComponentWordsForCompare(initialRows);

      if (!subantaChanged && !wordsChanged) {
        message.info('No changes to save');
        closeModal();
        return;
      }

      setSaving(true);
      if (subantaChanged) {
        await updateSubanta(head.id, patch);
      }
      if (wordsChanged) {
        await syncSamastaWordsForEdit(head.id, hasFlag, main.componentWords, initialRows);
      }
      message.success('Subanta updated');
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) {
        message.error('Fix the highlighted fields before saving.');
        return;
      }
      if (err instanceof Error && err.message.includes('subantaWordId must not equal')) {
        message.error(err.message);
        return;
      }
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const closeDetailModal = useCallback(() => {
    setDetailOpen(false);
    setDetail(null);
    setDetailRups([]);
  }, []);

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    const cached = getCachedSubantaDetail(id);
    if (cached) {
      setDetail(cached);
      setDetailLoading(false);
    } else {
      setDetail(null);
      setDetailLoading(true);
    }
    setDetailRups([]);
    try {
      const s = await getSubanta(id);
      const { changed, next } = mergeSubantaDetailCache(id, s);
      if (changed || !cached) setDetail(next);
      try {
        setDetailRups((await listRups({ subantaId: id })).filter(isRup));
      } catch {
        message.warning('Could not load rup entries for this subanta');
        setDetailRups([]);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load subanta');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteSubanta(id);
      invalidateSubantaDetail(id);
      message.success('Subanta deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const openMergeModal = useCallback(() => {
    mergeForm.resetFields();
    setMergeModalOpen(true);
  }, [mergeForm]);

  const closeMergeModal = useCallback(() => {
    setMergeModalOpen(false);
    mergeForm.resetFields();
  }, [mergeForm]);

  const submitMerge = useCallback(async () => {
    try {
      const { srcId, destinationId } = await mergeForm.validateFields();
      if (srcId === destinationId) {
        message.error('Source and destination must be different subantas.');
        return;
      }
      setMergeSaving(true);
      await mergeSubantas(srcId, destinationId);
      invalidateSubantaDetail(srcId);
      invalidateSubantaDetail(destinationId);
      message.success('Subanta merged successfully');
      closeMergeModal();
      setDetailOpen(false);
      setDetail(null);
      setDetailRups([]);
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Merge failed');
    } finally {
      setMergeSaving(false);
    }
  }, [closeMergeModal, load, mergeForm]);

  const closeSamastaEdit = () => {
    setSamastaEditOpen(false);
    setSamastaEditSubantaId(null);
    setSamastaEditHeadLabel('');
    setSamastaEditSortedWords([]);
    setSamastaEditInitialCompoundIds(new Set());
    samastaEditForm.resetFields();
  };

  const openEditSamasta = async (record: Subanta) => {
    setSamastaEditOpen(true);
    setSamastaEditLoading(true);
    setSamastaEditSubantaId(null);
    samastaEditForm.resetFields();
    try {
      const s = await getSubanta(record.id);
      mergeSubantaDetailCache(record.id, s);
      const { sortedWords, initialPairs } = parseSamastaDetailForEdit(s);
      const hasAny =
        sortedWords.length > 0 || (Array.isArray(s.samastas) && s.samastas.length > 0);
      if (!hasAny) {
        message.info('No samāsa data on this subanta.');
        closeSamastaEdit();
        return;
      }
      setSamastaEditSubantaId(s.id);
      setSamastaEditHeadLabel(`${s.word} (${s.iast}) · #${s.id}`);
      setSamastaEditSortedWords(sortedWords);
      setSamastaEditInitialCompoundIds(
        new Set(initialPairs.map((p) => p.compoundId).filter((id): id is number => typeof id === 'number'))
      );
      samastaEditForm.setFieldsValue({ pairs: initialPairs });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load subanta');
      closeSamastaEdit();
    } finally {
      setSamastaEditLoading(false);
    }
  };

  const submitSamastaPairsEdit = async () => {
    if (samastaEditSubantaId == null) return;
    const allowedWordIds = new Set(samastaEditSortedWords.map((w) => w.id));
    try {
      const { pairs } = await samastaEditForm.validateFields();
      const rows = (pairs ?? []) as SamastaPairEditValues[];
      if (rows.length > 0 && allowedWordIds.size < 2) {
        message.error(
          'Need at least two samasta_word rows to define pairs. Add component words first, then edit compounds.'
        );
        return;
      }
      for (const row of rows) {
        const fw = Number(row.first_wordId);
        const sw = Number(row.second_wordId);
        if (!allowedWordIds.has(fw) || !allowedWordIds.has(sw)) {
          message.error(
            'Each side of a pair must be one of the loaded samasta_word rows. Choose from the dropdowns.'
          );
          return;
        }
        if (fw === sw) {
          message.error('First and second component must be two different samasta_word rows.');
          return;
        }
      }
      setSamastaEditSaving(true);
      const nextIds = new Set(
        rows.map((r) => r.compoundId).filter((id): id is number => typeof id === 'number')
      );
      for (const id of Array.from(samastaEditInitialCompoundIds)) {
        if (!nextIds.has(id)) {
          await deleteSamastaCompound(id);
        }
      }
      for (const row of rows) {
        const first_wordId = Number(row.first_wordId);
        const second_wordId = Number(row.second_wordId);
        const subantaId = samastaEditSubantaId;
        if (row.compoundId != null) {
          await updateSamastaCompound(row.compoundId, {
            type: row.type,
            first_wordId,
            second_wordId,
            subantaId,
          });
        } else {
          await createSamastaCompound({
            type: row.type,
            first_wordId,
            second_wordId,
            subantaId,
          });
        }
      }
      message.success('Samāsa pairs saved');
      closeSamastaEdit();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSamastaEditSaving(false);
    }
  };

  const relationJson = useMemo(() => {
    if (!detail) return '';
    const rel = {
      vyutpattis: detail.vyutpattis,
      nispattis: detail.nispattis,
      arthas: detail.arthas,
      samastaWords: detail.samastaWords,
      samastas: detail.samastas,
      rups: detail.rups,
      media: detail.media,
      stats: detail.stats,
      notifications: detail.notifications,
    };
    return JSON.stringify(rel, null, 2);
  }, [detail]);

  const detailSamastaWordsSorted = useMemo(() => {
    if (!detail?.samastaWords || !Array.isArray(detail.samastaWords)) return [];
    return [...detail.samastaWords]
      .filter(isPlainObject)
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
  }, [detail]);

  const detailSamastasRows = useMemo(() => {
    if (!detail?.samastas || !Array.isArray(detail.samastas)) return [];
    return detail.samastas.filter(isPlainObject);
  }, [detail]);

  const detailArthas = useMemo(() => {
    const raw = detail?.arthas;
    if (!Array.isArray(raw)) return [];
    return raw.filter(isArtha);
  }, [detail?.arthas]);

  /** Languages that appear in detail arthas, with tab labels and counts. */
  const detailArthaTabsMeta = useMemo(() => {
    const byLang = new Map<number, Artha[]>();
    for (const a of detailArthas) {
      const list = byLang.get(a.languageId) ?? [];
      list.push(a);
      byLang.set(a.languageId, list);
    }
    const sortedIds = Array.from(new Set(detailArthas.map((a) => a.languageId))).sort((x, y) => x - y);
    const items = sortedIds.map((id) => {
      const list = byLang.get(id) ?? [];
      const first = list[0];
      let label = `Language #${id}`;
      if (first?.language && typeof first.language.name === 'string') {
        label = `${first.language.name} (${first.language.code})`;
      } else {
        const L = languageRows.find((l) => l.id === id);
        if (L) label = `${L.name} (${L.code})`;
      }
      return { key: String(id), label: `${label} · ${list.length}` };
    });
    return { items, sortedIds };
  }, [detailArthas, languageRows]);

  const detailArthaTabEffectiveKey = useMemo(() => {
    const ids = detailArthaTabsMeta.sortedIds;
    if (ids.length === 0) return '';
    const n = Number(detailArthaLanguageTabKey);
    if (detailArthaLanguageTabKey !== '' && ids.includes(n)) return detailArthaLanguageTabKey;
    return String(ids[0]);
  }, [detailArthaLanguageTabKey, detailArthaTabsMeta.sortedIds]);

  const detailArthaGroupsForActiveLanguageTab = useMemo(() => {
    if (!detailArthaTabEffectiveKey) return [] as Array<{ key: string; label: string; rows: Artha[] }>;
    const lid = Number(detailArthaTabEffectiveKey);
    const byOrigin = new Map<string, Artha[]>();
    const labelByOrigin = new Map<string, string>();
    for (const a of detailArthas) {
      if (a.languageId !== lid) continue;
      const key = arthaOriginGroupKey(a.origin);
      const label = arthaOriginGroupLabel(a.origin);
      const list = byOrigin.get(key) ?? [];
      list.push(a);
      byOrigin.set(key, list);
      if (!labelByOrigin.has(key)) labelByOrigin.set(key, label);
    }
    const keys = Array.from(byOrigin.keys()).sort((x, y) => {
      if (x === '__no_origin__') return 1;
      if (y === '__no_origin__') return -1;
      return x.localeCompare(y, undefined, { sensitivity: 'base', numeric: true });
    });
    return keys.map((key) => ({
      key,
      label: labelByOrigin.get(key) ?? 'No origin',
      rows: [...(byOrigin.get(key) ?? [])].sort(compareArthaDisplayOrder),
    }));
  }, [detailArthas, detailArthaTabEffectiveKey]);

  const reloadDetailIfOpen = useCallback(async () => {
    const id = detail?.id;
    if (id == null) return;
    try {
      const [s, rups] = await Promise.all([
        getSubanta(id),
        listRups({ subantaId: id }).catch(() => [] as Rup[]),
      ]);
      const { changed, next } = mergeSubantaDetailCache(id, s);
      if (changed) setDetail(next);
      setDetailRups(rups.filter(isRup));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to refresh subanta');
    }
  }, [detail?.id]);

  const moveDetailArtha = useCallback(
    async (row: Artha, dir: -1 | 1) => {
      const lid = row.languageId;
      const originKey = arthaOriginGroupKey(row.origin);
      const sorted = [...detailArthas].filter(
        (a) => a.languageId === lid && arthaOriginGroupKey(a.origin) === originKey
      );
      sorted.sort(compareArthaDisplayOrder);
      const idx = sorted.findIndex((a) => a.id === row.id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= sorted.length) return;
      const swapped = [...sorted];
      const tmp = swapped[idx];
      swapped[idx] = swapped[j];
      swapped[j] = tmp;
      setArthaReorderSaving(true);
      try {
        const patches = swapped.map((a, order) => {
          const prevOrder = a.order ?? null;
          return prevOrder !== order ? updateArtha(a.id, { order }) : Promise.resolve(null);
        });
        await Promise.all(patches);
        message.success('Artha order saved');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to save order');
      } finally {
        setArthaReorderSaving(false);
      }
    },
    [detailArthas, reloadDetailIfOpen]
  );

  const closeArthaModal = useCallback(() => {
    setArthaModalOpen(false);
    setArthaEditing(null);
    setArthaHeadSubantaId(null);
    arthaForm.resetFields();
  }, [arthaForm]);

  const openArthaCreate = useCallback(
    (subantaId: number) => {
      if (languageRows.length === 0) {
        message.warning('No languages loaded. Create languages via the API first.');
        return;
      }
      setArthaEditing(null);
      setArthaHeadSubantaId(subantaId);
      arthaForm.resetFields();
      arthaForm.setFieldsValue({
        languageId: languageRows[0].id,
        text: '',
        vyutpatti: '',
        example: '',
        antonyms: '',
        synonyms: '',
        tags: '',
        bidha: ARTHA_BIDHA[0],
        prakar: 'samanya',
      });
      setArthaModalOpen(true);
    },
    [arthaForm, languageRows]
  );

  const openArthaEdit = useCallback(
    (a: Artha) => {
      setArthaEditing(a);
      setArthaHeadSubantaId(a.subantaId);
      arthaForm.setFieldsValue({
        languageId: a.languageId,
        text: a.text,
        vyutpatti: a.vyutpatti ?? '',
        example: a.example ?? '',
        antonyms: a.antonyms ?? '',
        synonyms: a.synonyms ?? '',
        tags: a.tags ?? '',
        bidha: a.bidha ?? ARTHA_BIDHA[0],
        prakar: a.prakar ?? 'samanya',
      });
      setArthaModalOpen(true);
    },
    [arthaForm]
  );

  const submitArthaModal = useCallback(async () => {
    try {
      const values = await arthaForm.validateFields();
      const headId = arthaHeadSubantaId;
      if (headId == null) return;
      setArthaModalSaving(true);
      const body = arthaFormToCreateBody(headId, values);
      if (arthaEditing) {
        const patch = buildArthaPatch(arthaEditing, body);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeArthaModal();
          return;
        }
        await updateArtha(arthaEditing.id, patch);
        message.success('Artha updated');
      } else {
        await createArtha(body);
        message.success('Artha created');
      }
      closeArthaModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setArthaModalSaving(false);
    }
  }, [
    arthaEditing,
    arthaForm,
    arthaHeadSubantaId,
    closeArthaModal,
    reloadDetailIfOpen,
  ]);

  const handleDeleteArtha = useCallback(
    async (id: number) => {
      try {
        await deleteArtha(id);
        message.success('Artha deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const closeRupModal = useCallback(() => {
    setRupModalOpen(false);
    setRupEditing(null);
    setRupHeadSubantaId(null);
    setRupPrefillLoading(false);
    setRupFormulaBanner(null);
    setRupFormulaSearchLoading(false);
    rupCreatePreferredLingaRef.current = null;
    setRupTripleCreateUi(false);
    rupForm.resetFields();
  }, [rupForm]);

  const openRupCreate = useCallback(() => {
    const subantaId = detail?.id;
    if (subantaId == null) return;
    const used = new Set(detailRups.map((r) => r.linga));

    if (detail?.linga === 'trilinga' && isTrilingaTripleSlotsAvailable(detailRups)) {
      setRupTripleCreateUi(true);
      rupCreatePreferredLingaRef.current = null;
      setRupFormulaBanner(null);
      setRupEditing(null);
      setRupHeadSubantaId(subantaId);
      rupForm.resetFields();
      setRupModalOpen(true);
      return;
    }

    setRupTripleCreateUi(false);
    const headLinga = detail?.linga;
    const preferred =
      headLinga != null && headLinga !== 'trilinga' && !used.has(headLinga)
        ? headLinga
        : SUBANTA_LINGA.find((l) => !used.has(l));
    if (preferred == null) {
      message.warning('This subanta already has a rup for every liṅga.');
      return;
    }
    setRupFormulaBanner(null);
    setRupEditing(null);
    setRupHeadSubantaId(subantaId);
    rupCreatePreferredLingaRef.current = preferred;
    rupForm.resetFields();
    setRupModalOpen(true);
  }, [detail?.id, detail?.linga, detailRups, rupForm]);

  const openRupEdit = useCallback(
    async (r: Rup) => {
      setRupTripleCreateUi(false);
      setRupFormulaBanner(null);
      setRupFormulaSearchLoading(false);
      setRupEditing(r);
      setRupHeadSubantaId(r.subantaId);
      rupForm.resetFields();
      setRupModalOpen(true);
      setRupPrefillLoading(true);
      try {
        const fresh = await getRup(r.id);
        setRupEditing(fresh);
        rupForm.setFieldsValue(rupToFormValues(fresh));
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Failed to load rup');
        closeRupModal();
      } finally {
        setRupPrefillLoading(false);
      }
    },
    [closeRupModal, rupForm]
  );

  useEffect(() => {
    if (!rupModalOpen || rupEditing != null || !detail || rupTripleCreateUi) {
      if (!rupModalOpen || rupEditing != null) {
        setRupFormulaBanner(null);
        setRupFormulaSearchLoading(false);
      }
      return;
    }
    const linga = watchedRupLinga;
    if (!linga || typeof linga !== 'string') {
      setRupFormulaBanner(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        if (cancelled) return;
        setRupFormulaSearchLoading(true);
        setRupFormulaBanner(null);
        try {
          const batTrim =
            detail.bat != null && String(detail.bat).trim() !== ''
              ? String(detail.bat).trim()
              : '';
          const formulas = await searchSubantaRupFormula({
            prakar: detail.prakar,
            linga: linga as SubantaLinga,
            natwa: detail.natwa,
            bat: batTrim === '' ? '' : batTrim,
          });
          if (cancelled) return;
          const blank = Object.fromEntries(RUP_DECLENSION_KEYS.map((k) => [k, undefined]));
          if (formulas.length === 0) {
            rupForm.setFieldsValue({
              linga: linga as SubantaLinga,
              ...blank,
            });
            setRupFormulaBanner(
              'No matching subanta rup formula for this prakār, liṅga, natva, and bat.'
            );
            return;
          }
          const chosen = formulas[0];
          const stem =
            detail.base_word.trim() || detail.word.trim() || null;
          const cells = subantaRupFormulaGridToRupDeclensionFields(chosen, stem);
          rupForm.setFieldsValue({
            linga: linga as SubantaLinga,
            ...blank,
            ...cells,
          });
          setRupFormulaBanner(
            formulas.length > 1
              ? `Declension grid filled from subanta rup formula #${chosen.id} (first of ${formulas.length} matches).`
              : `Declension grid filled from subanta rup formula #${chosen.id}.`
          );
        } catch (err: unknown) {
          if (cancelled) return;
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.warning(
            ax?.response?.data?.message || ax?.message || 'Could not search subanta rup formula'
          );
          setRupFormulaBanner(null);
        } finally {
          if (!cancelled) setRupFormulaSearchLoading(false);
        }
      })();
    }, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [detail, rupEditing, rupForm, rupModalOpen, rupTripleCreateUi, watchedRupLinga]);

  useEffect(() => {
    if (!rupModalOpen || rupEditing != null || !detail || !rupTripleCreateUi) {
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        if (cancelled) return;
        setRupFormulaSearchLoading(true);
        setRupFormulaBanner(null);
        try {
          const batTrim =
            detail.bat != null && String(detail.bat).trim() !== ''
              ? String(detail.bat).trim()
              : '';
          const triple: NonNullable<RupModalFormValues['triple']> = {};
          const blanks = Object.fromEntries(RUP_DECLENSION_KEYS.map((k) => [k, undefined])) as Partial<
            Record<RupDeclensionKey, string | undefined>
          >;
          let matched = 0;
          for (const lk of TRILINGA_RUP_LINGAS) {
            const formulas = await searchSubantaRupFormula({
              prakar: detail.prakar,
              linga: lk,
              natwa: detail.natwa,
              bat: batTrim === '' ? '' : batTrim,
            });
            if (cancelled) return;
            if (formulas.length === 0) {
              triple[lk] = { ...blanks };
              continue;
            }
            matched += 1;
            const chosen = formulas[0];
            const stem =
              detail.base_word.trim() || detail.word.trim() || null;
            const cells = subantaRupFormulaGridToRupDeclensionFields(chosen, stem);
            triple[lk] = { ...blanks, ...cells };
          }
          if (!cancelled) {
            rupForm.setFieldsValue({ triple });
            if (matched === 0) {
              setRupFormulaBanner(
                'No matching subanta rup formula for any of puṃ / strī / napuṃsaka with this prakār, natva, and bat.'
              );
            } else {
              setRupFormulaBanner(
                matched === TRILINGA_RUP_LINGAS.length
                  ? 'Declension grids filled from subanta rup formulas for all three liṅgas.'
                  : `Declension grids partly filled (${matched} of ${TRILINGA_RUP_LINGAS.length} liṅgas had formula matches).`
              );
            }
          }
        } catch (err: unknown) {
          if (cancelled) return;
          const ax = err as { response?: { data?: { message?: string } }; message?: string };
          message.warning(
            ax?.response?.data?.message || ax?.message || 'Could not search subanta rup formula'
          );
          setRupFormulaBanner(null);
        } finally {
          if (!cancelled) setRupFormulaSearchLoading(false);
        }
      })();
    }, 320);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [detail, rupEditing, rupForm, rupModalOpen, rupTripleCreateUi]);

  const submitRupModal = useCallback(async () => {
    try {
      const headId = rupHeadSubantaId;
      if (headId == null) return;
      setRupModalSaving(true);

      if (rupTripleCreateUi && !rupEditing) {
        const values = await rupForm.validateFields();
        const triple = values.triple;
        await Promise.all(
          TRILINGA_RUP_LINGAS.map((lk) =>
            createRup(createRupBodyFromForm(headId, rupValuesFromTripleRowCells(lk, triple?.[lk])))
          )
        );
        message.success('Three rup rows created (puṃlinga, strīliṅga, napuṃsakaliṅga)');
        closeRupModal();
        await reloadDetailIfOpen();
        return;
      }

      const values = await rupForm.validateFields();
      if (rupEditing) {
        const patch = buildRupPatch(rupEditing, values as RupFormValues);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeRupModal();
          return;
        }
        await updateRup(rupEditing.id, patch);
        message.success('Rup updated');
      } else {
        await createRup(createRupBodyFromForm(headId, values as RupFormValues));
        message.success('Rup created');
      }
      closeRupModal();
      await reloadDetailIfOpen();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setRupModalSaving(false);
    }
  }, [
    closeRupModal,
    reloadDetailIfOpen,
    rupEditing,
    rupForm,
    rupHeadSubantaId,
    rupTripleCreateUi,
  ]);

  const handleDeleteRup = useCallback(
    async (id: number) => {
      try {
        await deleteRup(id);
        message.success('Rup deleted');
        await reloadDetailIfOpen();
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { message?: string } }; message?: string };
        message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
      }
    },
    [reloadDetailIfOpen]
  );

  const rupCreateLingaOptions = useMemo(() => {
    const used = new Set(detailRups.map((r) => r.linga));
    return lingaOpts.filter((o) => !used.has(o.value));
  }, [detailRups, lingaOpts]);

  const canOpenRupCreate = useMemo(() => {
    if (!detail) return false;
    if (detail.linga === 'trilinga' && isTrilingaTripleSlotsAvailable(detailRups)) return true;
    return rupCreateLingaOptions.length > 0;
  }, [detail, detailRups, rupCreateLingaOptions]);

  const detailArthaColumns: ColumnsType<Artha> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      {
        title: 'Order',
        key: 'ord',
        width: 72,
        render: (_, r) => (r.order ?? '—'),
      },
      {
        title: 'Language',
        key: 'lang',
        width: 140,
        ellipsis: true,
        render: (_, r) => {
          const nested = r.language;
          if (nested && typeof nested.name === 'string') {
            return `${nested.name} (${nested.code})`;
          }
          const L = languageRows.find((l) => l.id === r.languageId);
          return L ? `${L.name} (${L.code})` : `#${r.languageId}`;
        },
      },
      {
        title: 'Text',
        dataIndex: 'text',
        render: (text: string) => (
          <Typography.Paragraph
            style={{ marginBottom: 0, maxWidth: 520 }}
            ellipsis={{ rows: 3, expandable: true }}
          >
            {text}
          </Typography.Paragraph>
        ),
      },
      {
        title: 'Origin',
        dataIndex: 'origin',
        width: 100,
        ellipsis: true,
        render: (v: unknown) => (v == null || v === '' ? '—' : String(v)),
      },
      {
        title: 'Actions',
        key: 'arthaActions',
        width: 248,
        fixed: 'right',
        render: (_, r) => {
          const originKey = arthaOriginGroupKey(r.origin);
          const peers = [...detailArthas]
            .filter((a) => a.languageId === r.languageId && arthaOriginGroupKey(a.origin) === originKey)
            .sort(compareArthaDisplayOrder);
          const idx = peers.findIndex((a) => a.id === r.id);
          const canUp = idx > 0;
          const canDown = idx >= 0 && idx < peers.length - 1;
          return (
            <Space size="small" wrap>
              <Button
                type="text"
                size="small"
                aria-label="Move artha up"
                icon={<ArrowUpOutlined />}
                disabled={arthaReorderSaving || !canUp}
                onClick={() => void moveDetailArtha(r, -1)}
              />
              <Button
                type="text"
                size="small"
                aria-label="Move artha down"
                icon={<ArrowDownOutlined />}
                disabled={arthaReorderSaving || !canDown}
                onClick={() => void moveDetailArtha(r, 1)}
              />
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openArthaEdit(r)}>
                Edit
              </Button>
              <Popconfirm
                title="Delete this artha?"
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => void handleDeleteArtha(r.id)}
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [
      arthaReorderSaving,
      detailArthas,
      handleDeleteArtha,
      languageRows,
      moveDetailArtha,
      openArthaEdit,
    ]
  );

  const detailArthaColumnsWithoutLanguage = useMemo(
    () => detailArthaColumns.filter((col) => (col as { key?: string }).key !== 'lang'),
    [detailArthaColumns]
  );

  const detailRupsColumns: ColumnsType<Rup> = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', width: 64 },
      { title: 'Liṅga', dataIndex: 'linga', width: 180, ellipsis: true },
      {
        title: 'Actions',
        key: 'rupActions',
        width: 160,
        fixed: 'right',
        render: (_, r) => (
          <Space size="small">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void openRupEdit(r)}>
              Edit
            </Button>
            <Popconfirm
              title="Delete this rup?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => void handleDeleteRup(r.id)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleDeleteRup, openRupEdit]
  );

  const formatTs = (iso?: string) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  const handleSubantaTableChange = useCallback<NonNullable<TableProps<Subanta>['onChange']>>(
    (pagination, _filters, sorter) => {
      if (pagination?.current != null) setListPage(pagination.current);
      if (pagination?.pageSize != null) setListPageSize(pagination.pageSize);
      const s = Array.isArray(sorter) ? sorter[0] : sorter;
      const field = s?.field;
      const order = s?.order;
      if (isSubantaListSortField(field) && (order === 'ascend' || order === 'descend')) {
        setListTableSort({ field, order });
      } else {
        setListTableSort({ field: 'id', order: 'ascend' });
      }
    },
    []
  );

  const columns: ColumnsType<Subanta> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 64,
      sorter: (a, b) => Number(a.id) - Number(b.id),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'id' ? listTableSort.order : undefined,
    },
    {
      title: 'Word',
      dataIndex: 'word',
      ellipsis: true,
      width: 120,
      sorter: (a, b) => strSorter(a.word, b.word),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'word' ? listTableSort.order : undefined,
    },
    {
      title: 'Base',
      dataIndex: 'base_word',
      ellipsis: true,
      width: 100,
      sorter: (a, b) => strSorter(a.base_word, b.base_word),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'base_word'
          ? listTableSort.order
          : undefined,
    },
    {
      title: 'IAST',
      dataIndex: 'iast',
      ellipsis: true,
      width: 120,
      sorter: (a, b) => strSorter(a.iast, b.iast),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'iast' ? listTableSort.order : undefined,
    },
    {
      title: 'Bat',
      dataIndex: 'bat',
      ellipsis: true,
      width: 140,
      sorter: (a, b) => strSorter(a.bat, b.bat),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'bat' ? listTableSort.order : undefined,
      render: (value: string | null | undefined) =>
        value != null && String(value).trim() !== '' ? value : '—',
    },
    {
      title: 'Liṅga',
      dataIndex: 'linga',
      width: 130,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.linga, b.linga),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'linga' ? listTableSort.order : undefined,
    },
    {
      title: 'Barga',
      dataIndex: 'barga',
      width: 110,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.barga, b.barga),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'barga' ? listTableSort.order : undefined,
    },
    {
      title: 'Bachan',
      dataIndex: 'bachan',
      width: 120,
      ellipsis: true,
      sorter: (a, b) => strSorter(a.bachan, b.bachan),
      sortDirections: ['ascend', 'descend'],
      sortOrder:
        !listSortByHindiOrder && listTableSort.field === 'bachan' ? listTableSort.order : undefined,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 340,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void openDetail(record.id)}
          >
            View
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => void openEdit(record)}
          >
            Edit
          </Button>
          {hasSamastaListData(record) ? (
            <Button
              type="link"
              size="small"
              icon={<PartitionOutlined />}
              onClick={() => void openEditSamasta(record)}
            >
              Edit samāsa
            </Button>
          ) : null}
          <Popconfirm
            title="Delete this subanta?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Subanta"
      extra={
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search word, base, IAST, bat, liṅga…"
            style={{ width: 280 }}
            value={listSearchInput}
            onChange={(e) => setListSearchInput(e.target.value)}
          />
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Search in fields (empty = all)"
            style={{ width: 190 }}
            value={listSearchFields}
            options={searchFieldOptions}
            onChange={(value) => setListSearchFields(value as SubantaSearchField[])}
          />
          <Select
            style={{ width: 190 }}
            value={listSearchMode}
            options={searchModeOptions}
            onChange={(value) => setListSearchMode(value as SubantaSearchMode)}
          />
          <Select
            allowClear
            placeholder="Filter by liṅga (optional)"
            style={{ width: 220 }}
            value={listLingaFilter}
            options={lingaOpts}
            onChange={(value) => setListLingaFilter(value as SubantaLinga | undefined)}
          />
          <Checkbox
            checked={listSortByHindiOrder}
            onChange={(e) => setListSortByHindiOrder(e.target.checked)}
          >
            Sort by Hindi order
          </Checkbox>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add subanta
          </Button>
          <Button onClick={openMergeModal}>Merge subantas</Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        CRUD for <code>/subanta</code> on{' '}
        {process.env.REACT_APP_DICTIONARY_API_URL || 'https://koshaapidev.vag.org.np'}.
      </Typography.Paragraph>

      <Table<Subanta>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={displayedSubantaRows}
        onChange={handleSubantaTableChange}
        pagination={{
          current: listPage,
          pageSize: listPageSize,
          total: filteredSubantaRows.length,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => (
            <Space size={8} wrap>
              <Typography.Text type="secondary">
                {total ? `${range[0]}-${range[1]} of ${total}` : '0'}
              </Typography.Text>
              <Typography.Text type="secondary">Go to</Typography.Text>
              <Select
                size="small"
                style={{ width: 128 }}
                value={listPage}
                options={listPageOptions}
                showSearch
                optionFilterProp="label"
                onChange={(value) => setListPage(value)}
              />
            </Space>
          ),
        }}
        scroll={{ x: 1320 }}
      />

      <Modal
        title="Merge subantas"
        open={mergeModalOpen}
        onCancel={closeMergeModal}
        destroyOnClose
        width={640}
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeMergeModal}>Cancel</Button>
            <Button type="primary" danger loading={mergeSaving} onClick={() => void submitMerge()}>
              Merge
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          Calls <code>POST /subanta/merge</code>. This moves artha/vyutpatti/nispatti/notifications from source
          to destination, removes source-linked secondary rows, then deletes the source subanta.
        </Typography.Paragraph>
        <Form form={mergeForm} layout="vertical" preserve={false}>
          <Form.Item
            name="srcId"
            label="Source subanta (will be deleted)"
            rules={[{ required: true, message: 'Select source subanta' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={mergeSubantaOptions}
              placeholder="Choose source subanta"
            />
          </Form.Item>
          <Form.Item
            name="destinationId"
            label="Destination subanta (will be kept)"
            rules={[{ required: true, message: 'Select destination subanta' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={mergeSubantaOptions}
              placeholder="Choose destination subanta"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Subanta details"
        open={detailOpen}
        onCancel={closeDetailModal}
        footer={
          detailOpen ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span style={{ minWidth: 8 }} />
              {detailNav.total > 0 && detailNav.index >= 0 ? (
                <Typography.Text type="secondary">
                  {detailNav.index + 1} of {detailNav.total}
                </Typography.Text>
              ) : (
                <span />
              )}
              <Space wrap>
                <Button onClick={closeDetailModal}>Close</Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  disabled={!detail || detailLoading}
                  onClick={() => {
                    if (!detail) return;
                    const d = detail;
                    closeDetailModal();
                    void openEdit(d);
                  }}
                >
                  Edit
                </Button>
              </Space>
            </div>
          ) : null
        }
        width={920}
        styles={{
          body: {
            maxHeight: '85vh',
            overflow: 'hidden',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {detailLoading ? (
          <Typography.Text type="secondary" style={{ display: 'block', padding: 24 }}>
            Loading…
          </Typography.Text>
        ) : detail ? (
          <div
            style={{
              display: 'flex',
              flex: 1,
              minHeight: 0,
              flexDirection: 'row',
              alignItems: 'stretch',
              gap: 4,
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 44,
                paddingLeft: 6,
              }}
            >
              <Button
                type="default"
                shape="circle"
                icon={<LeftOutlined />}
                aria-label="Previous subanta"
                disabled={detailLoading || detailNav.prevId == null}
                onClick={() => {
                  if (detailNav.prevId != null) void openDetail(detailNav.prevId);
                }}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
              />
            </div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                padding: '12px 8px 12px 0',
              }}
            >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            <Typography.Text>
              <strong>Word:</strong> {detail.word}
            </Typography.Text>
            <Typography.Text>
              <strong>Base word:</strong> {detail.base_word}
            </Typography.Text>
            <Typography.Text>
              <strong>IAST:</strong> {detail.iast}
            </Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
              <Typography.Text strong>Bat:</Typography.Text>{' '}
              {detail.bat != null && String(detail.bat).trim() !== '' ? detail.bat : '—'}
            </Typography.Paragraph>
            <Typography.Text>
              <strong>Liṅga / vyutpanna / prakār / śreṇi:</strong> {detail.linga} · {detail.vyutpanna} ·{' '}
              {detail.prakar} · {detail.shreni}
            </Typography.Text>
            <Typography.Text>
              <strong>Śatva / natva / vedic / barga / bachan:</strong>{' '}
              {String(detail.shatwa)} · {String(detail.natwa)} · {detail.vedic} · {detail.barga} ·{' '}
              {detail.bachan}
            </Typography.Text>
            {(detail.createdAt ||
              detail.updatedAt ||
              detail.created_at ||
              detail.updated_at) && (
              <Space direction="vertical" size={0}>
                {(detail.createdAt || detail.created_at) && (
                  <Typography.Text>
                    <strong>Created:</strong>{' '}
                    {formatTs(detail.createdAt ?? detail.created_at)}
                  </Typography.Text>
                )}
                {(detail.updatedAt || detail.updated_at) && (
                  <Typography.Text>
                    <strong>Updated:</strong>{' '}
                    {formatTs(detail.updatedAt ?? detail.updated_at)}
                  </Typography.Text>
                )}
              </Space>
            )}

            <div>
              <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8 }}>
                Relations
              </Typography.Title>

              <Typography.Text strong>Samasta words</Typography.Text>
              {detailSamastaWordsSorted.length > 0 ? (
                <Table<Record<string, unknown>>
                  size="small"
                  style={{ marginTop: 8 }}
                  rowKey={(r) => String(r.id)}
                  columns={DETAIL_SAMASTA_WORD_COLUMNS}
                  dataSource={detailSamastaWordsSorted}
                  pagination={false}
                  scroll={{ x: true }}
                />
              ) : (
                <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                  none
                </Typography.Paragraph>
              )}

              <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
                Samāsa compounds
              </Typography.Text>
              {detailSamastasRows.length > 0 ? (
                <Table<Record<string, unknown>>
                  size="small"
                  style={{ marginTop: 8 }}
                  rowKey={(r) => String(r.id)}
                  columns={DETAIL_SAMASTA_COMPOUND_COLUMNS}
                  dataSource={detailSamastasRows}
                  pagination={false}
                  scroll={{ x: true }}
                />
              ) : (
                <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                  none
                </Typography.Paragraph>
              )}

              <Typography.Text strong style={{ display: 'block', marginTop: 16 }}>
                Arthas (meanings)
              </Typography.Text>
              <Space style={{ marginTop: 8, marginBottom: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openArthaCreate(detail.id)}
                >
                  Add artha
                </Button>
              </Space>
              {detailArthas.length > 0 ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Tabs
                    type="card"
                    size="small"
                    tabPlacement="top"
                    activeKey={detailArthaTabEffectiveKey}
                    onChange={setDetailArthaLanguageTabKey}
                    items={detailArthaTabsMeta.items}
                  />
                  {detailArthaGroupsForActiveLanguageTab.length > 0 ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      {detailArthaGroupsForActiveLanguageTab.map((g) => (
                        <div key={g.key}>
                          <Typography.Text strong>
                            Origin: {g.label} ({g.rows.length})
                          </Typography.Text>
                          <Table<Artha>
                            size="small"
                            rowKey="id"
                            loading={arthaReorderSaving}
                            columns={detailArthaColumnsWithoutLanguage}
                            dataSource={g.rows}
                            pagination={false}
                            scroll={{ x: 720 }}
                            style={{ marginTop: 8 }}
                          />
                        </div>
                      ))}
                    </Space>
                  ) : (
                    <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                      none
                    </Typography.Paragraph>
                  )}
                </Space>
              ) : (
                <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                  none
                </Typography.Paragraph>
              )}
              <Space style={{ marginTop: 8, marginBottom: 8 }}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={openRupCreate}
                  disabled={!canOpenRupCreate}
                >
                  Add rup
                </Button>
              </Space>
              {detailRups.length > 0 ? (
                <Table<Rup>
                  size="small"
                  rowKey="id"
                  columns={detailRupsColumns}
                  dataSource={detailRups}
                  pagination={false}
                  scroll={{ x: 480 }}
                  expandable={{
                    expandedRowRender: (record) => <RupDeclensionInspectExpanded r={record} />,
                  }}
                />
              ) : (
                <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
                  none
                </Typography.Paragraph>
              )}

              <div style={{ marginTop: 16 }}>
                {renderOtherRelationSection('Vyutpattis', detail.vyutpattis)}
                {renderOtherRelationSection('Nispattis', detail.nispattis)}
                <EntryMediaPanel
                  variant="subanta"
                  parentId={detail.id}
                  rawMedia={detail.media}
                  onReload={reloadDetailIfOpen}
                />
                <EntryStatsPanel
                  variant="subanta"
                  parentId={detail.id}
                  rawStats={detail.stats}
                  onReload={reloadDetailIfOpen}
                />
                <EntryNotificationsPanel
                  variant="subanta"
                  parentId={detail.id}
                  rawNotifications={detail.notifications}
                  onReload={reloadDetailIfOpen}
                />
              </div>

              <Collapse
                items={[
                  {
                    key: 'raw',
                    label: 'All relations (raw JSON)',
                    children: (
                      <pre
                        style={{
                          maxHeight: 320,
                          overflow: 'auto',
                          fontSize: 11,
                          background: '#f5f5f5',
                          padding: 12,
                          borderRadius: 8,
                          margin: 0,
                        }}
                      >
                        {relationJson}
                      </pre>
                    ),
                  },
                ]}
                style={{ marginTop: 8 }}
              />
            </div>
          </Space>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 44,
                paddingRight: 6,
              }}
            >
              <Button
                type="default"
                shape="circle"
                icon={<RightOutlined />}
                aria-label="Next subanta"
                disabled={detailLoading || detailNav.nextId == null}
                onClick={() => {
                  if (detailNav.nextId != null) void openDetail(detailNav.nextId);
                }}
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Edit samāsa pairs"
        open={samastaEditOpen}
        onCancel={closeSamastaEdit}
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeSamastaEdit}>Cancel</Button>
            <Button type="primary" loading={samastaEditSaving} onClick={() => void submitSamastaPairsEdit()}>
              Save pairs
            </Button>
          </Space>
        }
        width={720}
        destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {samastaEditLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Typography.Text>
              <strong>Head:</strong> {samastaEditHeadLabel || '—'}
            </Typography.Text>
            {samastaEditSortedWords.length > 0 ? (
              <div>
                <Typography.Text strong>Component words (same choices as in the pair dropdowns)</Typography.Text>
                <ul style={{ margin: '8px 0 0', paddingInlineStart: 20 }}>
                  {samastaEditSortedWords.map((w) => (
                    <li key={w.id}>
                      {formatSamastaWordSlotDisplay(w, rows, tingantaRows)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <Typography.Paragraph type="secondary">
                No <code>samasta_words</code> rows loaded. You can remove existing compounds below or fix data on
                the server.
              </Typography.Paragraph>
            )}
            {samastaEditSortedWords.length < 2 ? (
              <Typography.Text type="warning">
                At least two component words are required to add new pairs. Existing compounds can still be
                deleted when you save.
              </Typography.Text>
            ) : null}
            <Form form={samastaEditForm} layout="vertical" preserve={false} initialValues={{ pairs: [] }}>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                Dropdowns show the linked <strong>subanta</strong> or <strong>tiṅganta</strong> word; saved values
                are still <code>first_wordId</code> / <code>second_wordId</code> as <strong>samasta_word</strong> row
                ids. New rows use <code>POST /samasta</code>; removed rows <code>DELETE</code>; changed rows{' '}
                <code>PATCH</code>.
              </Typography.Paragraph>
              <Form.List name="pairs">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline" wrap>
                        <Form.Item {...restField} name={[name, 'compoundId']} hidden>
                          <InputNumber />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'type']}
                          rules={[{ required: true, message: 'type required' }]}
                        >
                          <Select
                            style={{ width: 200 }}
                            placeholder="compound type"
                            options={samastaTypeOpts}
                            showSearch
                            optionFilterProp="label"
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'first_wordId']}
                          rules={[{ required: true, message: 'First component required' }]}
                        >
                          <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder="First component (by samasta_word row)"
                            options={samastaWordSlotOptionsForEdit}
                            style={{ minWidth: 280 }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'second_wordId']}
                          rules={[{ required: true, message: 'Second component required' }]}
                        >
                          <Select
                            showSearch
                            allowClear
                            optionFilterProp="label"
                            placeholder="Second component (by samasta_word row)"
                            options={samastaWordSlotOptionsForEdit}
                            style={{ minWidth: 280 }}
                          />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => {
                        const w = samastaEditSortedWords;
                        add({
                          type: SAMASTA_TYPE[0],
                          first_wordId: w[0]?.id ?? 0,
                          second_wordId: w[1]?.id ?? w[0]?.id ?? 0,
                        });
                      }}
                      block
                      icon={<PlusOutlined />}
                      disabled={samastaEditSortedWords.length < 2}
                    >
                      Add pair
                    </Button>
                  </>
                )}
              </Form.List>
            </Form>
          </Space>
        )}
      </Modal>

      <Modal
        title={editing ? 'Edit subanta' : 'Add subanta'}
        open={modalOpen}
        onCancel={closeModal}
        footer={
          editing ? (
            <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button htmlType="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="primary"
                loading={saving}
                disabled={editPrefillLoading}
                onClick={() => void onSubmitEdit()}
              >
                Save
              </Button>
            </Space>
          ) : (
            <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={closeModal}>Cancel</Button>
              {hasSamastaWordsWatched ? (
                <Button
                  type="primary"
                  loading={saving}
                  onClick={() => void goToSamastaPairsStep()}
                >
                  Next: save & samāsa pairs
                </Button>
              ) : (
                <Button
                  type="primary"
                  loading={saving}
                  onClick={() => void submitCreateWithoutSamanta()}
                >
                  Create subanta
                </Button>
              )}
            </Space>
          )
        }
        destroyOnClose
        width={880}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Spin spinning={Boolean(editing && editPrefillLoading)} tip="Loading samāsa words…">
          <Form
            id="subanta-main-form"
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={{
              bat: null,
              shatwa: false,
              natwa: false,
              vedic: Vedic.LAUKIK,
              hasSamastaWords: false,
              componentWords: [],
            }}
            onFinish={() => {
              if (editing) {
                void onSubmitEdit();
              }
            }}
          >
            <Form.Item name="word" label="Word" rules={[{ required: true }]}>
              <Input placeholder="e.g. रामः" />
            </Form.Item>
          <Form.Item name="base_word" label="Base word" rules={[{ required: true }]}>
            <Input placeholder="e.g. राम" />
          </Form.Item>
          <Form.Item name="iast" label="IAST" rules={[{ required: true }]}>
            <Input placeholder="e.g. rāmaḥ" />
          </Form.Item>
          <Form.Item name="bat" label="Bat (optional)">
            <Input.TextArea
              rows={2}
              placeholder="Optional note; leave empty to store null"
              allowClear
            />
          </Form.Item>
          <Form.Item name="linga" label="Liṅga" rules={[{ required: true }]}>
            <Select
              placeholder="Select liṅga (e.g. pumalinga)"
              options={lingaOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="vyutpanna" label="Vyutpanna" rules={[{ required: true }]}>
            <Select
              placeholder="Select vyutpanna (e.g. vyutpanna)"
              options={vyutOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="prakar" label="Prakār" rules={[{ required: true }]}>
            <Select
              placeholder="Select prakār (e.g. adanata)"
              options={prakarOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="shreni" label="Śreṇi" rules={[{ required: true }]}>
            <Select
              placeholder="Select śreṇi (e.g. samanya)"
              options={shreniOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="shatwa" label="Śatva" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="natwa" label="Natva" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="vedic" label="Vedic Laukik" rules={[{ required: true }]}>
            <Select
              placeholder="Select vedic (e.g. laukik)"
              options={vedicOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="barga" label="Barga" rules={[{ required: true }]}>
            <Select
              placeholder="Select barga (e.g. naam)"
              options={bargaOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="bachan" label="Bachan" rules={[{ required: true }]}>
            <Select
              placeholder="Select bachan (e.g. ekbachan)"
              options={bachanOpts}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

            <Typography.Title level={5} style={{ marginTop: 16 }}>
              Samāsa (optional)
            </Typography.Title>
            <Form.Item name="hasSamastaWords" valuePropName="checked">
              <Checkbox disabled={Boolean(editing && editPrefillLoading)}>
                {editing ? (
                  <>
                    This subanta has samasta component words — edit rows below; <strong>Save</strong> updates{' '}
                    <code>/samasta-words</code>. Use <strong>Edit samāsa</strong> on the table for compound pairs.
                  </>
                ) : (
                  <>
                    This subanta has samasta component words — <strong>Next</strong> saves the subanta and
                    component rows, then opens the same samāsa pair editor as <strong>Edit samāsa</strong> on the
                    table
                  </>
                )}
              </Checkbox>
            </Form.Item>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.hasSamastaWords !== cur.hasSamastaWords}>
              {({ getFieldValue }) =>
                getFieldValue('hasSamastaWords') ? (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Typography.Text strong>Component words</Typography.Text>
                      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
                        Each row sets <code>order</code> and chooses <strong>exactly one</strong> component:
                        either a <code>subantaWordId</code> (another subanta) or a <code>tingantaWordId</code>{' '}
                        (tiṅganta). Rows are sorted by <code>order</code> for persistence.
                        {!editing ? (
                          <>
                            {' '}
                            After <strong>Next</strong>, you add compounds in the samāsa pair dialog (dropdowns by
                            component word).
                          </>
                        ) : null}{' '}
                        For subanta components, <code>subantaWordId</code> must not equal the compound head{' '}
                        <code>subantaId</code>. Unchecking clears existing component rows on save.
                      </Typography.Paragraph>
                      <Form.List name="componentWords">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map(({ key, name, ...restField }) => (
                              <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                <Form.Item {...restField} name={[name, 'samastaWordRowId']} hidden>
                                  <Input />
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'componentKind']}
                                  rules={[{ required: true, message: 'Choose component type' }]}
                                >
                                  <Select
                                    options={COMPONENT_KIND_OPTIONS}
                                    placeholder="Type"
                                    style={{ width: 128 }}
                                    disabled={Boolean(editing && editPrefillLoading)}
                                    onChange={() => {
                                      form.setFieldValue(['componentWords', name, 'refId'], undefined);
                                    }}
                                  />
                                </Form.Item>
                                <Form.Item
                                  noStyle
                                  dependencies={[['componentWords', name, 'componentKind']]}
                                >
                                  {() => {
                                    const kind =
                                      (form.getFieldValue([
                                        'componentWords',
                                        name,
                                        'componentKind',
                                      ]) as ComponentKind | undefined) ?? 'subanta';
                                    return (
                                      <Form.Item
                                        {...restField}
                                        name={[name, 'refId']}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              kind === 'tinganta'
                                                ? 'Select a tiṅganta'
                                                : 'Select a subanta',
                                          },
                                        ]}
                                      >
                                        <Select
                                          showSearch
                                          allowClear
                                          optionFilterProp="label"
                                          placeholder={
                                            kind === 'tinganta' ? 'Tiṅganta' : 'Subanta'
                                          }
                                          options={
                                            kind === 'tinganta'
                                              ? tingantaWordSelectOptions
                                              : subantaWordSelectOptions
                                          }
                                          style={{ minWidth: 260 }}
                                          disabled={Boolean(editing && editPrefillLoading)}
                                        />
                                      </Form.Item>
                                    );
                                  }}
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'order']}
                                  rules={[{ required: true, message: 'order required' }]}
                                >
                                  <InputNumber
                                    min={0}
                                    style={{ width: 120 }}
                                    placeholder="order"
                                    disabled={Boolean(editing && editPrefillLoading)}
                                  />
                                </Form.Item>
                                <MinusCircleOutlined
                                  onClick={() => remove(name)}
                                  style={{
                                    opacity: editing && editPrefillLoading ? 0.35 : 1,
                                    pointerEvents: editing && editPrefillLoading ? 'none' : undefined,
                                  }}
                                />
                              </Space>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() =>
                                add({
                                  componentKind: 'subanta',
                                  order: fields.length,
                                })
                              }
                              block
                              icon={<PlusOutlined />}
                              disabled={Boolean(editing && editPrefillLoading)}
                            >
                              Add component word
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </div>
                  </Space>
                ) : null
              }
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      <Modal
        title={arthaEditing ? 'Edit artha' : 'Add artha'}
        open={arthaModalOpen}
        onCancel={closeArthaModal}
        destroyOnClose
        width={640}
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeArthaModal}>Cancel</Button>
            <Button type="primary" loading={arthaModalSaving} onClick={() => void submitArthaModal()}>
              Save
            </Button>
          </Space>
        }
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          <code>POST/PATCH /artha</code> — <code>prakār</code> uses the same values as subanta śreṇi.
        </Typography.Paragraph>
        <Form form={arthaForm} layout="vertical" preserve={false}>
          <Form.Item name="languageId" label="Language" rules={[{ required: true, message: 'Choose language' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Language"
              options={languageSelectOptions}
            />
          </Form.Item>
          <Form.Item name="text" label="Text (meaning)" rules={[{ required: true, message: 'Meaning text required' }]}>
            <Input.TextArea rows={3} placeholder="Meaning text" />
          </Form.Item>
          <Form.Item name="vyutpatti" label="Vyutpatti (etymology)">
            <Input.TextArea rows={2} placeholder="Etymology" />
          </Form.Item>
          <Form.Item name="example" label="Example">
            <Input.TextArea rows={2} placeholder="Usage example" />
          </Form.Item>
          <Form.Item name="antonyms" label="Antonyms">
            <Input placeholder="e.g. ant1, ant2" />
          </Form.Item>
          <Form.Item name="synonyms" label="Synonyms">
            <Input placeholder="e.g. syn1, syn2" />
          </Form.Item>
          <Form.Item name="tags" label="Tags">
            <Input placeholder="e.g. tag1,tag2" />
          </Form.Item>
          <Form.Item name="bidha" label="Bidha" rules={[{ required: true, message: 'Select bidha' }]}>
            <Select showSearch optionFilterProp="label" options={bidhaOpts} placeholder="Bidha" />
          </Form.Item>
          <Form.Item name="prakar" label="Prakār (śreṇi)" rules={[{ required: true, message: 'Select prakār' }]}>
            <Select showSearch optionFilterProp="label" options={shreniOpts} placeholder="Prakār" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          rupEditing ? `Edit rup #${rupEditing.id}` : rupTripleCreateUi ? 'Add rup (trilinga)' : 'Add rup'
        }
        open={rupModalOpen}
        onCancel={closeRupModal}
        afterOpenChange={(open) => {
          if (!open) return;
          const pref = rupCreatePreferredLingaRef.current;
          if (pref == null) return;
          rupForm.setFieldsValue({ linga: pref });
          rupCreatePreferredLingaRef.current = null;
        }}
        destroyOnClose
        width={rupTripleCreateUi ? 1024 : 960}
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={closeRupModal}>Cancel</Button>
            <Button type="primary" loading={rupModalSaving} onClick={() => void submitRupModal()}>
              Save
            </Button>
          </Space>
        }
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
      >
        <Spin spinning={rupPrefillLoading || (!rupEditing && rupFormulaSearchLoading)}>
          <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
            Declension strings use the same field names as <code>POST /rup</code> (e.g.{' '}
            <code>prathama_ekbachan</code>).
          </Typography.Paragraph>
          <Form form={rupForm} layout="vertical" preserve={false}>
            {!rupTripleCreateUi ? (
              <Form.Item name="linga" label="Liṅga" rules={[{ required: true, message: 'Select liṅga' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={rupEditing ? lingaOpts : rupCreateLingaOptions}
                  disabled={Boolean(rupEditing)}
                  placeholder="Liṅga"
                />
              </Form.Item>
            ) : null}
            {!rupEditing ? (
              <>
                <Typography.Paragraph style={{ marginBottom: 8 }}>
                  <Typography.Text strong>Base word (subanta):</Typography.Text>{' '}
                  <Typography.Text code>
                    {detail != null &&
                    detail.base_word != null &&
                    String(detail.base_word).trim() !== ''
                      ? detail.base_word
                      : '—'}
                  </Typography.Text>
                </Typography.Paragraph>
                
                {rupFormulaBanner ? (
                  <Alert
                    type={rupFormulaBanner.startsWith('No matching') ? 'info' : 'success'}
                    message={rupFormulaBanner}
                    showIcon
                    style={{ marginBottom: 12 }}
                  />
                ) : null}
              </>
            ) : null}
            {rupTripleCreateUi && !rupEditing ? (
              <>
                {TRILINGA_RUP_LINGAS.map((lk) => (
                  <div key={lk} style={{ marginBottom: 28 }}>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                      {trilingaRupSectionTitle(lk)} (<code>{lk}</code>)
                    </Typography.Title>
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 13,
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                width: 108,
                                border: '1px solid #f0f0f0',
                                padding: 4,
                                background: '#fafafa',
                              }}
                            />
                            {RUP_TRIPLE.map((t) => (
                              <th
                                key={t}
                                style={{
                                  border: '1px solid #f0f0f0',
                                  padding: 6,
                                  background: '#fafafa',
                                }}
                              >
                                {t}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {RUP_VIBHAKTI.map((v) => (
                            <tr key={`${lk}-${v}`}>
                              <td
                                style={{
                                  border: '1px solid #f0f0f0',
                                  padding: 4,
                                  background: '#fafafa',
                                  fontWeight: 500,
                                }}
                              >
                                {v}
                              </td>
                              {RUP_TRIPLE.map((t) => {
                                const cell = `${v}_${t}` as RupDeclensionKey;
                                return (
                                  <td key={cell} style={{ border: '1px solid #f0f0f0', padding: 4 }}>
                                    <Form.Item name={['triple', lk, cell]} noStyle>
                                      <Input placeholder={cell} size="small" />
                                    </Form.Item>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <Typography.Text strong>Declension grid</Typography.Text>
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            width: 108,
                            border: '1px solid #f0f0f0',
                            padding: 4,
                            background: '#fafafa',
                          }}
                        />
                        {RUP_TRIPLE.map((t) => (
                          <th
                            key={t}
                            style={{
                              border: '1px solid #f0f0f0',
                              padding: 6,
                              background: '#fafafa',
                            }}
                          >
                            {t}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {RUP_VIBHAKTI.map((v) => (
                        <tr key={v}>
                          <td
                            style={{
                              border: '1px solid #f0f0f0',
                              padding: 4,
                              background: '#fafafa',
                              fontWeight: 500,
                            }}
                          >
                            {v}
                          </td>
                          {RUP_TRIPLE.map((t) => {
                            const name = `${v}_${t}` as const;
                            return (
                              <td key={name} style={{ border: '1px solid #f0f0f0', padding: 4 }}>
                                <Form.Item name={name} noStyle>
                                  <Input placeholder={name} size="small" />
                                </Form.Item>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Form>
        </Spin>
      </Modal>
    </Card>
  );
}
